import React, { Ref, forwardRef, RefAttributes } from 'react';
import RsReactSelect, {
  ActionMeta,
  GroupBase,
  OnChangeValue,
} from 'react-select';
import Select from 'react-select/dist/declarations/src/Select';
import { StateManagerProps } from 'react-select/dist/declarations/src/stateManager';
import { isArray } from 'tradevan-ui/consts';
import HelperText, { HelperTextProps } from 'tradevan-ui/Form/HelperText';
import { Ioption } from 'tradevan-ui/type';

const errorBorder = '#eb6d71';
export interface ReactSelectProps<
  T extends Ioption,
  IsMulti extends boolean = false,
  Group extends GroupBase<T> = GroupBase<T>,
> extends Omit<StateManagerProps<T, IsMulti, Group>, 'onChange' | 'value'>,
    RefAttributes<Select<T, IsMulti, Group>>,
    HelperTextProps {
  value?: Ioption['value'];
  onChange?: (newValue: Ioption['value'], actionMeta: ActionMeta<T>) => void;
  options: T[];
  // options: OptionsOrGroups<T, Group>;
}

export type ReactSelectRef<
  T,
  IsMulti extends boolean = false,
  Group extends GroupBase<T> = GroupBase<T>,
> = Ref<Select<T, IsMulti, Group>>;

/** react-select */
const ReactSelect = forwardRef(
  <T extends Ioption, IsMulti extends boolean, Group extends GroupBase<T>>(
    props: ReactSelectProps<T, IsMulti, Group>,
    ref: ReactSelectRef<T, IsMulti, Group>,
  ): JSX.Element => {
    const { styles, value, onChange, options, error, helperText, ...other } =
      props;
    const valueOption = options.find((obj) => obj.value === value);

    return (
      <>
        <RsReactSelect
          styles={{
            control: (provided, state) => {
              let boxShadow = state.isFocused
                ? 'inset 0 1px 1px #80ceff, 0 0 8px #80ceff'
                : provided.boxShadow;
              let borderColor = state.isFocused
                ? '#46beed'
                : provided.borderColor;
              let hoverBorderColor = provided?.[':hover']?.borderColor;
              if (error) {
                boxShadow = state.isFocused
                  ? '0 0 0 0.2rem rgb(235 109 113 / 25%)'
                  : 'none';
                borderColor = errorBorder;
                hoverBorderColor = state.isFocused ? '#ec5155' : errorBorder;
              }

              return {
                ...provided,
                ...(error ? { borderColor: errorBorder } : {}),
                boxShadow,
                borderColor,
                borderRadius: '10px',
                ':hover': {
                  boxShadow: error
                    ? '0 0 0 0.2rem rgb(235 109 113 / 25%)'
                    : provided?.[':hover']?.boxShadow,
                  borderColor: hoverBorderColor,
                },
              };
            },
            menuPortal: (base) => ({ ...base, zIndex: 5 }),
            menu: (provided) => ({
              ...provided,
              borderRadius: '10px',
              marginTop: '0px',
            }),
            menuList: (provided) => ({
              ...provided,
              borderRadius: '10px',
            }),
            option: (provided, state) => ({
              ...provided,
              padding: '4px 12px',
              backgroundColor: state.isSelected
                ? '#2d97eb'
                : provided.backgroundColor,
            }),
            indicatorSeparator: (provided) => ({
              ...provided,
              width: 0,
            }),
            dropdownIndicator: (provided) => ({
              ...provided,
              color: '#2d97eb',
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: '2px 0 2px 8px',
            }),
            ...styles,
          }}
          ref={ref}
          isSearchable={false}
          menuPosition="fixed"
          placeholder="請選擇…"
          value={valueOption}
          onChange={(newValue, actionMeta) => {
            const temp = newValue as OnChangeValue<T, false>;
            const newActionMeta: ActionMeta<T> = {
              ...actionMeta,
              option: newValue as T,
            };
            if (onChange) {
              if (!temp) onChange('', newActionMeta);
              else if (!isArray(temp)) onChange(temp.value, newActionMeta);
            }
          }}
          {...{ options, ...other }}
        />
        <HelperText {...{ error, helperText }} />
      </>
    );
  },
);

ReactSelect.displayName = 'ReactSelect';
export default ReactSelect;
